/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Claim request parser
 */

import _ from 'lodash';
import {
	payerResponsibilitySequenceMapping,
	institutionalCareTeamCodeMapping as careTeamCodeMapping,
	institutionalIdentifierKeyAndCodeMapping as identifierKeyAndCodeMapping,
	HCPCS_SYSTEMS,
	MODIFIER_CODES_KEYS,
} from 'app-constants/claims/claimRequest';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';

// helper functions
const isStartByHash = (str: string): boolean => {
	return _.startsWith(str, '#');
};

const removeFirstHash = (str: string): string => {
	return _.slice(str, 1).join('');
};

// get fhir resource
const getFhirResource = (data: ClaimRequest.IClaim, valueReference: string): AnyType => {
	if (isStartByHash(valueReference)) {
		return _.find(data?.fhirResource?.contained, (contained) => contained?.id === removeFirstHash(valueReference));
	}
	return _.get(data?.entities, valueReference);
};

// format date
const formatDate = (dateString: string | undefined): string => {
	if (!dateString) {
		return '';
	}
	// Remove any 'T' and everything after it if exists
	const cleanDate = _.split(dateString, 'T')[0];

	// Split the date into parts
	const [year, month, day] = _.split(cleanDate, '-');

	// Return in MM/DD/YYYY format
	return `${month}/${day}/${year}`;
};

// separate date and time
const separateDateAndTime = (isoString: string): { date: string; time: string } => {
	const [datePart, timePart] = _.split(isoString, 'T');
	const time = _.first(_.split(timePart, '+'));
	return {
		date: datePart,
		time,
	};
};

// get name meta
const getNameMeta = (nameEntry: Fhir.HumanName[]): InstitutionalClaimParser.NameMeta[] => {
	return _.map(nameEntry, (name) => {
		const nameMeta = {} as InstitutionalClaimParser.NameMeta;
		nameMeta.suffix = name?.suffix;
		nameMeta.prefix = name?.prefix;
		nameMeta.lastName = name?.family;
		if (name?.given) {
			nameMeta.firstName = name?.given?.[0];
			// Store additional given names if available
			if (name?.given?.length > 1) {
				nameMeta.middleNames = _.slice(name?.given, 1);
			}
		}
		nameMeta.fullName = _.compact(
			_.flatten([nameMeta.prefix, nameMeta.firstName, nameMeta.middleNames, nameMeta.lastName, nameMeta.suffix]),
		).join(' ');
		return nameMeta;
	});
};

// get contacts meta
const getContacts = (contacts: Fhir.Contacts[]): InstitutionalClaimParser.ContactPointMeta[] => {
	return _.map(contacts, (contact) => {
		return {
			name: getNameMeta(contact?.name),
			telecom: _.map(contact?.telecom, (telecom) => telecom),
			address: contact?.address,
		};
	});
};

// find value by identifier code(system and value)
const findValueByIdentifierCode = (
	identifiers: Fhir.Identifier[],
	codeToMatch: string,
	codeSystem: string,
	valueSystem: string,
): InstitutionalClaimParser.IdentifierResponse => {
	if (!_.isArray(identifiers)) {
		return null;
	}
	const matchingIdentifier = _.find(identifiers, (identifier: Fhir.Identifier) => {
		return (
			identifier?.system === valueSystem &&
			_.some(identifier?.type?.coding, (coding) => coding?.code === codeToMatch && coding?.system === codeSystem)
		);
	});
	if (matchingIdentifier) {
		const matchingCoding = _.find(
			matchingIdentifier?.type?.coding,
			(coding) => coding?.code === codeToMatch && coding?.system === codeSystem,
		);
		const value = matchingIdentifier?.value;
		if (value) {
			return {
				value,
				display: matchingCoding?.display,
				code: matchingCoding?.code,
			};
		} else {
			return null;
		}
	}
	return null;
};

// populate identifier meta( here we will have identifier in parent resource level and fallback identifier in resource level
//  but always prioritize parent resource identifier)
const populateIdentifierMeta = ({
	mappingConfig,
	targetMeta,
	identifiers,
	fallbackIdentifiers,
}: {
	mappingConfig: AnyRecord;
	targetMeta: AnyRecord;
	identifiers?: Fhir.Identifier[];
	fallbackIdentifiers?: Fhir.Identifier[];
}): void => {
	_.forEach(mappingConfig, (mappings, key) => {
		// Try each mapping option for this key until we find a value
		for (const mapping of mappings) {
			const codeSystem = mapping?.codeSystem;
			const valueSystem = mapping?.valueSystem;
			let identifierValue = null;
			if (identifiers) {
				identifierValue = findValueByIdentifierCode(identifiers, mapping?.code, codeSystem, valueSystem);
			}
			if (!identifierValue && fallbackIdentifiers) {
				identifierValue = findValueByIdentifierCode(fallbackIdentifiers, mapping?.code, codeSystem, valueSystem);
			}
			if (identifierValue) {
				targetMeta[key] = identifierValue;
				break;
			}
		}
	});
};

// find code by type
const findCodeByType = (types: Fhir.Type[], system: string): string | null => {
	if (!_.isArray(types)) {
		return null;
	}
	return _.compact(
		_.map(types, (type) => {
			return _.find(type?.coding, (coding) => coding?.system === system);
		}),
	)[0]?.code;
};

// get billable period
const getBillablePeriod = (data: ClaimRequest.IClaim): Fhir.Period | undefined => {
	if (data?.fhirResource?.billablePeriod) {
		const start = data?.fhirResource?.billablePeriod?.start;
		const end = data?.fhirResource?.billablePeriod?.end;
		return {
			start: formatDate(start),
			end: formatDate(end),
		};
	}
};

// get patient meta
const getPatient = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IPatient => {
	const patient = data?.fhirResource?.patient;
	const parsedPatient = getFhirResource(data, patient?.reference) as Fhir.Patient;
	if (parsedPatient) {
		const patientMeta: InstitutionalClaimParser.IPatient = {};
		patientMeta.id = parsedPatient?.id;
		patientMeta.gender = parsedPatient?.gender;
		patientMeta.birthDate = formatDate(parsedPatient?.birthDate);
		patientMeta.address = parsedPatient?.address;
		if (parsedPatient?.name) {
			patientMeta.nameMeta = getNameMeta(parsedPatient.name);
		}
		populateIdentifierMeta({
			mappingConfig: identifierKeyAndCodeMapping.patient,
			targetMeta: patientMeta,
			identifiers: [patient?.identifier],
			fallbackIdentifiers: parsedPatient?.identifier,
		});
		return patientMeta;
	}
	return undefined;
};

// get payer meta
const getPayer = (parsedPayer?: Fhir.Organization, isPrimary: boolean = true): InstitutionalClaimParser.IPayer => {
	if (parsedPayer) {
		const payerMeta: InstitutionalClaimParser.IPayer = {};
		payerMeta.name = parsedPayer?.name;
		payerMeta.contact = getContacts(parsedPayer?.contact || []);

		// Loop through identifier mappings to populate payer metadata
		populateIdentifierMeta({
			mappingConfig: identifierKeyAndCodeMapping[isPrimary ? 'insurer' : 'otherInsurer'],
			targetMeta: payerMeta,
			identifiers: parsedPayer?.identifier,
		});

		return payerMeta;
	}
	return undefined;
};

// get billing provider meta
const getBillingProvider = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IBillingProvider => {
	const parsedBillingProvider = getFhirResource(data, data?.fhirResource?.provider?.reference) as Fhir.Organization | Fhir.Practitioner;
	if (parsedBillingProvider) {
		const billingProviderMeta: InstitutionalClaimParser.IBillingProvider = {};
		if (_.isString(parsedBillingProvider.name)) {
			billingProviderMeta.name = parsedBillingProvider.name;
		} else if (!_.isEmpty(parsedBillingProvider.name)) {
			billingProviderMeta.nameMeta = getNameMeta(parsedBillingProvider.name);
		}

		billingProviderMeta.contact = getContacts((parsedBillingProvider as Fhir.Organization).contact ?? []);
		billingProviderMeta.address = (parsedBillingProvider as Fhir.Practitioner).address ?? [];

		populateIdentifierMeta({
			mappingConfig: identifierKeyAndCodeMapping.billingProvider,
			targetMeta: billingProviderMeta,
			identifiers: [data?.fhirResource?.provider?.identifier],
			fallbackIdentifiers: parsedBillingProvider.identifier,
		});

		if ((parsedBillingProvider as Fhir.Organization).type) {
			billingProviderMeta.taxonomyCode = findCodeByType(_.get(parsedBillingProvider, 'type', []), fhirSystemMapping['PRV-03']);
		}
		return billingProviderMeta;
	}
	return undefined;
};

// get receiver and submitter meta
const getReceiverAndSubmitterMeta = (
	resource: Fhir.Practitioner | Fhir.Organization,
	isReceiver: boolean,
): InstitutionalClaimParser.ISubmitter => {
	const meta = {} as InstitutionalClaimParser.ISubmitter;
	if (_.isString(resource?.name)) {
		meta.name = resource?.name;
	} else if (!_.isEmpty(resource?.name)) {
		meta.nameMeta = getNameMeta(resource?.name);
	}
	meta.contact = getContacts((resource as Fhir.Organization)?.contact ?? []);
	meta.address = (resource as Fhir.Practitioner)?.address ?? [];
	populateIdentifierMeta({
		mappingConfig: identifierKeyAndCodeMapping[isReceiver ? 'receiver' : 'submitter'],
		targetMeta: meta,
		identifiers: resource?.identifier,
	});
	return meta;
};

// get care team meta
const getCareTeamMeta = (data: ClaimRequest.IClaim): InstitutionalClaimParser.ICareTeamMeta => {
	const careTeam = data?.fhirResource?.careTeam;
	if (_.isEmpty(careTeam)) {
		return undefined;
	}
	const careTeamMeta = {};
	_.forEach(careTeam, (careTeamEntry) => {
		const careTeamTypeCode = _.find(careTeamEntry?.role?.coding, (coding) =>
			_.includes(_.keys(careTeamCodeMapping), coding?.code),
		)?.code;
		const sequenceNumber = careTeamEntry?.sequence;
		if (careTeamTypeCode) {
			const key = _.get(careTeamCodeMapping, careTeamTypeCode)?.key;
			const type = _.get(careTeamCodeMapping, careTeamTypeCode)?.display;
			const reference = careTeamEntry?.provider?.reference;
			const resource = getFhirResource(data, reference);
			if (resource) {
				const resourceMeta = {} as InstitutionalClaimParser.ICareTeam;
				if (_.isString(resource?.name)) {
					resourceMeta.name = resource?.name;
				} else if (!_.isEmpty(resource?.name)) {
					resourceMeta.nameMeta = getNameMeta(_.cloneDeep(resource?.name));
				}
				resourceMeta.contact = getContacts(resource?.contact ?? []);
				resourceMeta.address = resource?.address ?? [];
				resourceMeta.type = type;
				resourceMeta.reference = reference;
				populateIdentifierMeta({
					mappingConfig: identifierKeyAndCodeMapping.careTeam,
					targetMeta: resourceMeta,
					identifiers: [careTeamEntry?.provider?.identifier],
					fallbackIdentifiers: resource?.identifier,
				});
				careTeamMeta[key] = {
					...(careTeamMeta?.[key] ?? {}),
					[sequenceNumber]: resourceMeta,
				};
			}
		}
	});
	return careTeamMeta;
};

// get extension resource meta(common meta for both institutional and professional claim)
const getExtensionResourceMeta = (data: ClaimRequest.IClaim): InstitutionalClaimParser.ExtensionMeta => {
	const extensionMeta = data?.fhirResource?.extension;
	const allMeta = {} as InstitutionalClaimParser.ExtensionMeta;
	const submitter = _.find(extensionMeta, (extension) => extension?.url === fhirSystemMapping.submitter)?.valueReference?.reference;
	const receiver = _.find(extensionMeta, (extension) => extension?.url === fhirSystemMapping.receiver)?.valueReference?.reference;
	const payToAddress = _.find(extensionMeta, (extension) => extension?.url === fhirSystemMapping.payToAddress)?.valueReference?.reference;
	const payToPlan = _.find(extensionMeta, (extension) => extension?.url === fhirSystemMapping.payToPlan)?.valueReference?.reference;

	if (submitter) {
		const submitterResource = getFhirResource(data, submitter);
		allMeta.submitter = getReceiverAndSubmitterMeta(submitterResource, false);
	}
	if (receiver) {
		const receiverResource = getFhirResource(data, receiver);
		allMeta.receiver = getReceiverAndSubmitterMeta(receiverResource, true);
	}
	if (payToAddress) {
		const payToAddressResource = getFhirResource(data, payToAddress);
		allMeta.payToAddress = {
			address: payToAddressResource?.address,
			contact: getContacts(payToAddressResource?.contact || []),
		};
	}
	if (payToPlan) {
		const payToPlanResource = getFhirResource(data, payToPlan);
		const payToPlanMeta: InstitutionalClaimParser.PayToPlan = {
			name: payToPlanResource?.name,
			contact: getContacts(payToPlanResource?.contact || []),
		};
		populateIdentifierMeta({
			mappingConfig: identifierKeyAndCodeMapping.payToPlan,
			targetMeta: payToPlanMeta,
			identifiers: payToPlanResource?.identifier,
		});
		allMeta.payToPlan = payToPlanMeta;
	}
	return allMeta;
};

// get extension meta(institutional specific meta)
const getExtensionMeta = (data: ClaimRequest.IClaim): InstitutionalClaimParser.ExtensionMeta => {
	const extensionMeta = data?.fhirResource?.extension;
	if (_.isArray(extensionMeta)) {
		const allMeta = getExtensionResourceMeta(data);
		const clSection = _.find(extensionMeta, (extension) => extension?.url === fhirSystemMapping.cl1);
		const clmSection = _.find(extensionMeta, (extension) => extension?.url === fhirSystemMapping.clm);

		const noteItems = _.filter(extensionMeta, (extension) => extension?.url === fhirSystemMapping.nte) || [];
		allMeta.note = _.map(noteItems, (item) => {
			const noteCode = _.find(item?.extension, (ext) => ext?.url === fhirSystemMapping['NTE-01'])?.valueCode;
			const noteText = _.find(item?.extension, (ext) => ext?.url === fhirSystemMapping['NTE-02'])?.valueString;
			return {
				noteCode,
				noteText,
			};
		});
		const dischargeExtension = _.find(
			extensionMeta,
			(extension) =>
				extension?.url === fhirSystemMapping.dtp &&
				_.some(extension?.extension, (ext) => _.some(ext.valueCodeableConcept?.coding, (coding) => coding?.code === '096')),
		);
		if (dischargeExtension) {
			const dischargeTime = _.find(dischargeExtension?.extension, (ext) => ext.url?.includes('DTP/DTP03'))?.valueTime;
			if (dischargeTime) {
				allMeta.dischargeTime = dischargeTime;
			}
		}
		if (clSection) {
			const codingItems = clSection?.valueCodeableConcept?.coding;
			const admissionTypeCode = _.find(codingItems, (coding) => coding?.system === fhirSystemMapping['CL1-01'])?.code;
			const admissionSourceCode = _.find(codingItems, (coding) => coding?.system === fhirSystemMapping['CL1-02'])?.code;
			const patientStatusCode = _.find(codingItems, (coding) => coding?.system === fhirSystemMapping['CL1-03'])?.code;
			allMeta.clSection = {
				admissionTypeCode,
				admissionSourceCode,
				patientStatusCode,
			};
		}
		const facilityTypeCode = _.find(extensionMeta, (extension) => extension?.url === fhirSystemMapping.facilityTypeCode)?.valueCode;
		const facilityCodeQualifier = _.find(
			extensionMeta,
			(extension) => extension?.url === fhirSystemMapping.facilityCodeQualifier,
		)?.valueCode;
		const claimFrequencyCode = _.find(extensionMeta, (extension) => extension?.url === fhirSystemMapping.frequencyCode)?.valueCode;
		allMeta.clmSection = {
			facilityTypeCode,
			facilityCodeQualifier,
			claimFrequencyCode,
		};
		if (clmSection) {
			const codingItems = clmSection?.valueCodeableConcept?.coding;
			const planAssignmentCode = _.find(codingItems, (coding) => coding?.system === fhirSystemMapping['CLM-07']);
			const benefitAssignmentIndicator = _.find(codingItems, (coding) => coding?.system === fhirSystemMapping['CLM-08']);
			const releaseOfInformationCode = _.find(codingItems, (coding) => coding?.system === fhirSystemMapping['CLM-09']);
			const delayReasonCode = _.find(codingItems, (coding) => coding?.system === fhirSystemMapping['CLM-20']);

			allMeta.clmSection = {
				...allMeta.clmSection,
				planAssignmentCode,
				benefitAssignmentIndicator,
				releaseOfInformationCode,
				delayReasonCode,
			};
		}
		return allMeta;
	}
	return undefined;
};

// get subscriber and payer info along with coverage info
const getSubscriberPayerInfo = (data: ClaimRequest.IClaim): InstitutionalClaimParser.ISubscriberAndPayer[] => {
	const parsedInsurance = _.orderBy(data?.fhirResource?.insurance, 'sequence', 'asc');
	const subscribersMeta = _.map(parsedInsurance, (insurance) => {
		const coverage = getFhirResource(data, insurance?.coverage?.reference) as Fhir.Coverage;
		// for secondary filing, need a fall back check inside coverage.contained as well
		let subscriber = getFhirResource(data, coverage?.subscriber?.reference) as Fhir.Patient;
		if (!subscriber && isStartByHash(coverage?.subscriber?.reference)) {
			const subscriberRefId = removeFirstHash(coverage.subscriber.reference);
			subscriber = _.find(coverage?.contained, (containedResource) => containedResource.id === subscriberRefId) as Fhir.Patient;
		}
		const payerResource = getFhirResource(data, coverage?.insurer?.reference) as Fhir.Organization;
		// payer info
		const payer = getPayer(payerResource, false);

		// subscriber info
		const subscriberInfo: InstitutionalClaimParser.ISubscriber = {};
		subscriberInfo.nameMeta = getNameMeta(subscriber?.name);
		subscriberInfo.address = subscriber?.address;
		subscriberInfo.gender = subscriber?.gender;
		subscriberInfo.birthDate = formatDate(subscriber?.birthDate);
		subscriberInfo.patientRelation = coverage?.relationship?.coding?.[0]?.display || 'Unknown';
		// memberId can be stored in coverage.subscriberId instead of coverage.identifier
		const subscriberIdEntries: Fhir.Identifier[] = _.compact(
			_.map(coverage?.subscriberId, (subscriberId: string | Fhir.Identifier) =>
				_.isString(subscriberId) ? { value: subscriberId } : subscriberId,
			),
		);
		populateIdentifierMeta({
			mappingConfig: identifierKeyAndCodeMapping.subscriber,
			targetMeta: subscriberInfo,
			identifiers: _.compact([...(coverage?.identifier ?? []), ...subscriberIdEntries]),
			fallbackIdentifiers: subscriber?.identifier,
		});

		// coverage info
		const coverageInfo: InstitutionalClaimParser.ICoverage = {};
		const payerTypeConcept = _.find(coverage?.extension, (ext) => ext.url === fhirSystemMapping['SBR-09'])?.valueCodeableConcept;
		const payerTypeCoding = _.find(payerTypeConcept?.coding, (coding) => coding?.system === fhirSystemMapping['ELEMENT-1032']);
		coverageInfo.payerTypeCoding = payerTypeCoding;
		coverageInfo.insuranceTypeCoding = _.find(coverage?.type?.coding, (coding) => coding?.system === fhirSystemMapping['ELEMENT-1336']);
		coverageInfo.planCode = coverage?.class?.[0]?.value?.value;
		coverageInfo.planName = coverage?.class?.[0]?.name;
		coverageInfo.sequenceNumber = insurance?.sequence;
		coverageInfo.payerResponsibility =
			_.get(payerResponsibilitySequenceMapping, insurance?.sequence)?.display || payerResponsibilitySequenceMapping?.unknown?.display;
		return { subscriber: subscriberInfo, payer, coverage: coverageInfo };
	});
	if (_.isArray(subscribersMeta)) {
		return subscribersMeta;
	}
	return undefined;
};

// get claim info
const getClaimInfo = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IClaimInfo => {
	const claimRequest = data?.fhirResource;
	const claimMeta = {} as InstitutionalClaimParser.IClaimInfo;
	claimMeta.status = claimRequest?.status;
	claimMeta.type = _.find(claimRequest?.type?.coding, (coding) => coding?.system === fhirSystemMapping.claimType)?.code;
	populateIdentifierMeta({
		mappingConfig: identifierKeyAndCodeMapping.claim,
		targetMeta: claimMeta,
		identifiers: claimRequest?.identifier,
	});
	claimMeta.patientControlNumber = _.find(claimRequest?.identifier, (identifier) =>
		_.includes(identifier?.system, fhirSystemMapping.patientControlNumber),
	)?.value;
	claimMeta.total = claimRequest?.total;
	claimMeta.payerResponsibilitySequence = _.find(claimRequest?.insurance, (insurance) => insurance?.focal)?.sequence;
	return claimMeta;
};

// get events meta
const getEventsMeta = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IEventsMeta => {
	const events = data?.fhirResource?.event;
	if (_.isEmpty(events)) {
		return undefined;
	}
	const eventsMeta = {} as InstitutionalClaimParser.IEventsMeta;
	_.forEach(events, (event) => {
		const hasAdmissionDateTime = _.some(event?.type?.coding, (coding) => coding?.code === '435');
		if (hasAdmissionDateTime) {
			const admissionDateTime = event?.whenDateTime;
			if (admissionDateTime) {
				const { date, time } = separateDateAndTime(admissionDateTime);
				eventsMeta.admissionDate = formatDate(date);
				eventsMeta.admissionHour = time;
			}
		}
	});
	return eventsMeta;
};

// get supporting info
const getSupportingInfo = (data: ClaimRequest.IClaim): InstitutionalClaimParser.ISupportingInfoMeta => {
	const supportingInfo = data?.fhirResource?.supportingInfo;
	const supportingInfoMeta = {
		occurrenceInfo: [],
		valueInfo: [],
		conditionInfo: [],
		occurrenceSpanInfo: [],
		patientReasonOfVisit: [],
		externalCauseOfInjury: [],
		pwkMeta: [],
	} as InstitutionalClaimParser.ISupportingInfoMeta;
	if (_.isArray(supportingInfo)) {
		_.forEach(supportingInfo, (info) => {
			// occurrence info
			const isOccurrenceInfo = _.some(
				info?.category?.coding,
				(coding) => coding?.code === 'BH' && coding?.system === fhirSystemMapping.claimInformationCategory,
			);
			if (isOccurrenceInfo) {
				const code = _.find(info?.code?.coding, (coding) => coding?.system === fhirSystemMapping.OccurrenceCode)?.code;
				const timingDate = info?.timingDate;
				if (code && timingDate) {
					supportingInfoMeta.occurrenceInfo.push({ code, timingDate: formatDate(timingDate) });
				}
			}
			// occurrence span info
			const isOccurrenceSpanInfo = _.some(
				info?.category?.coding,
				(coding) => coding?.code === 'BI' && coding?.system === fhirSystemMapping.claimInformationCategory,
			);
			if (isOccurrenceSpanInfo) {
				const code = _.find(info?.code?.coding, (coding) => coding?.system === fhirSystemMapping.OccurrenceSpan)?.code;
				const from = formatDate(info?.timingPeriod?.start);
				const through = formatDate(info?.timingPeriod?.end);
				if (code && from && through) {
					supportingInfoMeta.occurrenceSpanInfo.push({ code, from, through });
				}
			}

			// value info
			const isValueInfo = _.some(
				info?.category?.coding,
				(coding) => coding?.code === 'BE' && coding?.system === fhirSystemMapping.claimInformationCategory,
			);
			if (isValueInfo) {
				const code = _.find(info?.code?.coding, (coding) => coding?.system === fhirSystemMapping.ValueCodesAndAmounts)?.code;
				// not showing currency in the UI for now
				const value = `${info?.extension?.[0]?.valueMoney?.value}`;
				if (code && value) {
					supportingInfoMeta?.valueInfo?.push({ code, value });
				}
			}
			// condition info
			const isConditionInfo = _.some(
				info?.category?.coding,
				(coding) => coding?.code === 'BG' && coding?.system === fhirSystemMapping.claimInformationCategory,
			);
			if (isConditionInfo) {
				const code = _.first(info?.code?.coding)?.code;
				if (code) {
					supportingInfoMeta.conditionInfo.push(code);
				}
			}
			// patient reason of visit
			const isPatientReasonOfVisit = _.some(info?.category?.coding, (coding) => coding?.code === 'reason');
			if (isPatientReasonOfVisit) {
				const codeData = _.first(info?.code?.coding);
				if (codeData?.code) {
					supportingInfoMeta.patientReasonOfVisit.push({ code: codeData?.code, system: codeData?.system });
				}
			}
			// external cause of injury
			const isExternalCauseOfInjury = _.some(info?.category?.coding, (coding) => coding?.code === 'external-cause');
			if (isExternalCauseOfInjury) {
				const codeData = _.first(info?.code?.coding);
				if (codeData?.code) {
					const indicator = _.find(
						info?.extension,
						(ext) => ext.url === fhirSystemMapping.PresentOnAdmissionIndicator,
					)?.valueString;
					supportingInfoMeta.externalCauseOfInjury.push({
						code: codeData?.code,
						system: codeData?.system,
						systemMeta: codeData,
						indicator,
					});
				}
			}
			// pwk meta
			const isPwkMeta = _.some(info?.category?.coding, (coding) => coding?.code === 'PWK');
			if (isPwkMeta) {
				const attachmentReportTypeCode = _.find(
					info?.code?.coding,
					(coding) => coding?.system === fhirSystemMapping['ELEMENT-755'],
				);
				const attachmentReportTransmissionCode = _.find(
					info?.extension?.[0]?.valueCodeableConcept?.coding,
					(coding) => coding?.system === fhirSystemMapping['ELEMENT-756'],
				);
				const attachmentControlNumber =
					info?.valueIdentifier?.system === fhirSystemMapping['PWK-06'] ? info?.valueIdentifier?.value : undefined;
				supportingInfoMeta.pwkMeta.push({
					attachmentReportTypeCode,
					attachmentReportTransmissionCode,
					attachmentControlNumber,
				});
			}
		});
		return supportingInfoMeta;
	}
	return undefined;
};

// get diagnosis info
const getDiagnosisInfo = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IDiagnosisInfo => {
	const diagnosisInfo = data?.fhirResource?.diagnosis;
	if (_.isEmpty(diagnosisInfo)) {
		return undefined;
	}

	const diagnosisInfoMeta = { admitDx: [], principalDx: [], otherDx: [] } as InstitutionalClaimParser.IDiagnosisInfo;
	// check diagnosis code qualifier 66.DX
	const hasIcd9Code = _.some(diagnosisInfo, (diagnosis) => {
		const icd10System = _.find(diagnosis?.diagnosisCodeableConcept?.coding, (coding) => _.includes(coding?.system, 'icd-9'));
		return !_.isEmpty(icd10System);
	});
	const hasIcd10Code = _.some(diagnosisInfo, (diagnosis) => {
		const icd10System = _.find(diagnosis?.diagnosisCodeableConcept?.coding, (coding) => _.includes(coding?.system, 'icd-10'));
		return !_.isEmpty(icd10System);
	});
	if (hasIcd9Code) {
		diagnosisInfoMeta.diagnosisCodeQualifier = '9';
	} else if (hasIcd10Code) {
		diagnosisInfoMeta.diagnosisCodeQualifier = '0';
	} else {
		diagnosisInfoMeta.diagnosisCodeQualifier = '';
	}
	_.forEach(diagnosisInfo, (diagnosis) => {
		const isAdmittingDiagnosis = _.some(
			diagnosis?.type?.[0]?.coding,
			(coding) => coding?.code === 'admitting' && coding?.system === fhirSystemMapping.diagnosisType,
		);
		if (isAdmittingDiagnosis) {
			const codeData = _.first(diagnosis?.diagnosisCodeableConcept?.coding);
			if (codeData?.code) {
				diagnosisInfoMeta.admitDx.push({ code: codeData?.code, system: codeData?.system });
			}
		}
		const isPrincipalDiagnosis = _.some(
			diagnosis?.type?.[0]?.coding,
			(coding) => coding?.code === 'principal' && coding?.system === fhirSystemMapping.diagnosisType,
		);
		if (isPrincipalDiagnosis) {
			const codeData = _.first(diagnosis?.diagnosisCodeableConcept?.coding);
			const poaIndicator = _.find(
				diagnosis?.extension,
				(ext) => ext.url === fhirSystemMapping.PresentOnAdmissionIndicator,
			)?.valueString;
			if (codeData?.code) {
				diagnosisInfoMeta.principalDx.push({ code: codeData?.code, system: codeData?.system, poaIndicator });
			}
		}
		const isOtherDiagnosis = _.some(
			diagnosis?.type?.[0]?.coding,
			(coding) => coding?.code === 'other' && coding?.system === fhirSystemMapping.diagnosisType,
		);
		if (isOtherDiagnosis) {
			const codeData = _.first(diagnosis?.diagnosisCodeableConcept?.coding);
			const poaIndicator = _.find(
				diagnosis?.extension,
				(ext) => ext.url === fhirSystemMapping.PresentOnAdmissionIndicator,
			)?.valueString;
			if (codeData?.code) {
				diagnosisInfoMeta.otherDx.push({ code: codeData?.code, system: codeData?.system, poaIndicator });
			}
		}
	});
	return diagnosisInfoMeta;
};

// get items info(line item info)
const getItemsInfo = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IItemsInfo[] => {
	const items = data?.fhirResource?.item;
	const itemsInfo = [] as InstitutionalClaimParser.IItemsInfo[];
	if (_.isArray(items)) {
		_.forEach(items, (item) => {
			const itemInfo = {} as InstitutionalClaimParser.IItemsInfo;
			itemInfo.id = `item-${item?.sequence}`;
			const revCode = _.find(item?.revenue?.coding, (coding) => coding?.system === fhirSystemMapping.revenueCode);
			itemInfo.revenueCode = {
				code: revCode?.code,
				system: revCode?.system,
			};
			itemInfo.description = revCode?.display;
			const serviceStartDate = item?.servicedDate || item?.servicedPeriod?.start;
			const serviceEndDate = item?.servicedDate || item?.servicedPeriod?.end;
			itemInfo.servicePeriod = {
				start: formatDate(serviceStartDate),
				end: formatDate(serviceEndDate),
			};
			itemInfo.traceNumber = _.first(item?.traceNumber)?.value;
			itemInfo.quantity = item?.quantity;
			itemInfo.netAmount = item?.net;
			// todo update modifiers system when BE confirms
			const modifiers = _.map(MODIFIER_CODES_KEYS, (key, index) => {
				const modifier = item?.modifier?.[index];
				return {
					key,
					code: modifier?.coding?.[0]?.code || null,
					system: fhirSystemMapping.hcpcsCode,
				};
			});
			itemInfo.modifiers = modifiers;
			const hcpcsCode = _.find(item?.productOrService?.coding, (coding) => _.includes(HCPCS_SYSTEMS, coding?.system));
			itemInfo.HCPCSCode = {
				code: hcpcsCode?.code,
				system: hcpcsCode?.system,
			};
			itemInfo.nonCoveredAmount = _.find(item?.extension, (ext) => ext.url === fhirSystemMapping['SV2-207'])?.valueMoney;
			const subDetail = item?.detail?.[0]?.subDetail?.[0];
			itemInfo.ndc = {
				code: _.find(subDetail?.productOrService?.coding, (coding) => coding?.system === fhirSystemMapping.hcpcsCode)?.code,
				units: subDetail?.quantity?.value,
				measure: subDetail?.quantity?.code,
				charge: subDetail?.net,
			};
			itemsInfo.push(itemInfo);
		});
	}
	return itemsInfo;
};

// get service facility meta
const getServiceFacility = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IServiceFacility => {
	const serviceFacility = getFhirResource(data, data?.fhirResource?.facility?.reference) as Fhir.Location | Fhir.Organization;
	if (!_.isEmpty(serviceFacility)) {
		const serviceFacilityMeta = {} as InstitutionalClaimParser.IServiceFacility;
		serviceFacilityMeta.name = serviceFacility?.name;
		serviceFacilityMeta.address = (serviceFacility as Fhir.Location).address;
		serviceFacilityMeta.contact = getContacts(serviceFacility?.contact);
		populateIdentifierMeta({
			mappingConfig: identifierKeyAndCodeMapping.serviceFacility,
			targetMeta: serviceFacilityMeta,
			identifiers: serviceFacility?.identifier,
		});
		return serviceFacilityMeta;
	}
	return undefined;
};

// get procedure info( we will have one principal procedure and rest are other procedures)
const getProcedureInfo = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IProcedureInfo => {
	const procedures = data?.fhirResource?.procedure;
	const procedureInfo = { primaryProcedure: [], otherProcedure: [] };
	if (_.isArray(procedures)) {
		_.forEach(procedures, (procedure) => {
			const isPrimaryProcedure = _.some(procedure?.type?.[0]?.coding, (coding) => coding?.code === 'principal');
			if (isPrimaryProcedure) {
				const codeData = _.first(procedure?.procedureCodeableConcept?.coding);
				if (codeData?.code) {
					procedureInfo.primaryProcedure.push({
						code: codeData?.code,
						system: codeData?.system,
						date: formatDate(procedure?.date),
					});
				}
			} else {
				const codeData = _.first(procedure?.procedureCodeableConcept?.coding);
				if (codeData?.code) {
					procedureInfo.otherProcedure.push({
						code: codeData?.code,
						system: codeData?.system,
						date: formatDate(procedure?.date),
					});
				}
			}
		});
	}
	return procedureInfo;
};

// get diagnosis related group info (we only have one diagnosis related group in institutional claim)
const getDiagnosisRelatedGroupInfo = (data: ClaimRequest.IClaim): string => {
	return _.find(data?.fhirResource?.diagnosisRelatedGroup?.coding, (coding) => coding?.system === fhirSystemMapping.diagnosisRelatedGroup)
		?.code;
};

// get related claims
const getRelatedClaims = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IRelatedClaims[] => {
	const relatedClaims = data?.fhirResource?.related;
	if (_.isArray(relatedClaims)) {
		return _.map(relatedClaims, (relatedClaim) => {
			return {
				relationship: relatedClaim?.relationship?.coding?.[0]?.code,
				reference: relatedClaim?.reference?.value,
			};
		});
	}
};

// get institutional claim parser
const getInstitutionalClaimParser = (data: ClaimRequest.IClaim): InstitutionalClaimParser.IResponse => {
	const billablePeriod = getBillablePeriod(data);
	const patient = getPatient(data);
	const payer = getPayer(getFhirResource(data, data?.fhirResource?.insurer?.reference), true);
	const billingProvider = getBillingProvider(data);
	const subscribersAndPayers = getSubscriberPayerInfo(data);
	const extensionMeta = getExtensionMeta(data);
	const careTeamMeta = getCareTeamMeta(data);
	const claimInfo = getClaimInfo(data);
	const eventsMeta = getEventsMeta(data);
	const supportingInfoMeta = getSupportingInfo(data);
	const diagnosisInfo = getDiagnosisInfo(data);
	const itemsInfo = getItemsInfo(data);
	const serviceFacility = getServiceFacility(data);
	const procedureInfo = getProcedureInfo(data);
	const diagnosisRelatedGroupInfo = getDiagnosisRelatedGroupInfo(data);
	const relatedClaims = getRelatedClaims(data);
	const additionalLineItemTable = [];
	const claimAdjudicationInfo = null;

	return {
		billablePeriod,
		patient,
		payer,
		billingProvider,
		subscribersAndPayers,
		extensionMeta,
		careTeamMeta,
		claimInfo,
		eventsMeta,
		supportingInfoMeta,
		diagnosisInfo,
		itemsInfo,
		serviceFacility,
		procedureInfo,
		diagnosisRelatedGroupInfo,
		relatedClaims,
		additionalLineItemTable,
		claimAdjudicationInfo,
	};
};

export {
	getInstitutionalClaimParser,
	getBillablePeriod,
	getPayer,
	getSubscriberPayerInfo,
	getPatient,
	getBillingProvider,
	populateIdentifierMeta,
	getExtensionResourceMeta,
	getServiceFacility,
	getFhirResource,
	getNameMeta,
	findValueByIdentifierCode,
	getContacts,
	formatDate,
	removeFirstHash,
	isStartByHash,
	getRelatedClaims,
};
